import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

const MOBILE_BREAKPOINT = 750;
const DRAG_CLOSE_THRESHOLD = 90;
const DRAG_RESISTANCE = 0.85;

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];
  #abortController = new AbortController();
  #dragStartY = 0;
  #dragOffsetY = 0;
  #isDragging = false;
  #accordionAnimations = new WeakMap();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onAccordionHeadingClick);
    this.refs.menuDrawer.addEventListener('touchstart', this.#onTouchStart, {
      passive: true,
      signal: this.#abortController.signal,
    });
    this.refs.menuDrawer.addEventListener('touchmove', this.#onTouchMove, {
      passive: false,
      signal: this.#abortController.signal,
    });
    this.refs.menuDrawer.addEventListener('touchend', this.#onTouchEnd, { signal: this.#abortController.signal });
    this.refs.menuDrawer.addEventListener('touchcancel', this.#onTouchEnd, { signal: this.#abortController.signal });
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onAccordionHeadingClick);
    this.#abortController.abort();
  }

  #onAccordionHeadingClick = (event) => {
    if (!this.#isMobile() || !(event.target instanceof Element)) return;

    const summary = event.target.closest('summary');
    const details = summary?.parentElement;
    const accordion = details?.parentElement;
    if (
      !(summary instanceof HTMLElement) ||
      !(details instanceof HTMLDetailsElement) ||
      !(accordion instanceof HTMLElement) ||
      accordion.tagName !== 'ACCORDION-CUSTOM'
    ) {
      return;
    }

    const menu = accordion.closest('.menu-drawer__menu');
    if (!menu) return;

    event.preventDefault();

    if (details.open) {
      summary.setAttribute('aria-expanded', 'false');
      this.#animateAccordion(details, false);
      return;
    }

    const openSiblings = Array.from(menu.querySelectorAll('accordion-custom > details[open]')).filter((openDetails) => {
      const openMenu = openDetails.parentElement?.closest('.menu-drawer__menu');
      return openDetails !== details && openMenu === menu;
    });

    openSiblings.forEach((openDetails) => {
      openDetails.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      this.#animateAccordion(openDetails, false);
    });
    summary.setAttribute('aria-expanded', 'true');
    this.#animateAccordion(details, true);
  };

  /**
   * Animates a menu accordion without letting the details element hide its content early.
   * @param {HTMLDetailsElement} details
   * @param {boolean} opening
   */
  #animateAccordion(details, opening) {
    const content = details.querySelector(':scope > .details-content');
    if (!(content instanceof HTMLElement)) {
      details.open = opening;
      return;
    }

    const currentStyle = getComputedStyle(content);
    const currentHeight = content.getBoundingClientRect().height;
    const currentPaddingStart = parseFloat(currentStyle.paddingBlockStart) || 0;
    const currentPaddingEnd = parseFloat(currentStyle.paddingBlockEnd) || 0;
    this.#accordionAnimations.get(details)?.cancel();
    details.dataset.accordionSwitching = 'true';
    if (opening) details.open = true;

    const openStyle = getComputedStyle(content);
    const targetHeight = opening ? content.getBoundingClientRect().height : 0;
    const targetPaddingStart = opening ? parseFloat(openStyle.paddingBlockStart) || 0 : 0;
    const targetPaddingEnd = opening ? parseFloat(openStyle.paddingBlockEnd) || 0 : 0;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const animation = content.animate(
      [
        {
          blockSize: `${opening ? 0 : currentHeight}px`,
          paddingBlockStart: `${opening ? 0 : currentPaddingStart}px`,
          paddingBlockEnd: `${opening ? 0 : currentPaddingEnd}px`,
          opacity: opening ? 0 : 1,
        },
        {
          blockSize: `${targetHeight}px`,
          paddingBlockStart: `${targetPaddingStart}px`,
          paddingBlockEnd: `${targetPaddingEnd}px`,
          opacity: opening ? 1 : 0,
        },
      ],
      {
        duration: reducedMotion ? 0 : 480,
        easing: 'cubic-bezier(0.65, 0, 0.35, 1)',
        fill: 'both',
      }
    );

    this.#accordionAnimations.set(details, animation);
    animation.finished
      .then(() => {
        if (this.#accordionAnimations.get(details) !== animation) return;
        if (!opening) details.open = false;
        animation.cancel();
        delete details.dataset.accordionSwitching;
        this.#accordionAnimations.delete(details);
      })
      .catch(() => {});
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean}
   */
  #isMobile() {
    return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`).matches;
  }

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  #getScrollableParent(target) {
    if (!(target instanceof Element)) return null;

    return target.closest('.menu-drawer__navigation, .menu-drawer__inner-submenu');
  }

  /**
   * @param {TouchEvent} event
   */
  #onTouchStart = (event) => {
    if (!this.isOpen || !this.#isMobile() || event.touches.length !== 1) return;

    this.#dragStartY = event.touches[0].clientY;
    this.#dragOffsetY = 0;
    this.#isDragging = false;
    this.refs.menuDrawer.style.transition = 'none';
  };

  /**
   * @param {TouchEvent} event
   */
  #onTouchMove = (event) => {
    if (!this.isOpen || !this.#isMobile() || event.touches.length !== 1) return;

    const deltaY = event.touches[0].clientY - this.#dragStartY;
    const scrollableParent = this.#getScrollableParent(event.target);
    const canScrollUp = scrollableParent ? scrollableParent.scrollTop > 0 : false;

    if (deltaY <= 0 || canScrollUp) return;

    this.#isDragging = true;
    this.#dragOffsetY = deltaY * DRAG_RESISTANCE;
    event.preventDefault();
    this.refs.menuDrawer.style.transform = `translateY(${this.#dragOffsetY}px)`;
  };

  #onTouchEnd = () => {
    if (!this.#isDragging) return;

    const shouldClose = this.#dragOffsetY > DRAG_CLOSE_THRESHOLD;

    this.refs.menuDrawer.style.removeProperty('transition');
    requestAnimationFrame(() => {
      this.refs.menuDrawer.style.removeProperty('transform');

      if (shouldClose) {
        this.close();
      }
    });

    this.#isDragging = false;
    this.#dragOffsetY = 0;
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      this.refs.menuDrawer.style.removeProperty('transform');
      this.refs.menuDrawer.style.removeProperty('transition');
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
    this.refs.menuDrawer.style.removeProperty('transform');
    this.refs.menuDrawer.style.removeProperty('transition');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
